import { isAxiosError } from 'axios';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useEffect, useMemo } from 'react';

import { CmsRedirection } from '@/src/cms/domain/models/cms-redirection';
import { CmsContentForPathSSRFactory } from '@/src/cms/presentation/ssr/cms-content-for-path-ssr-factory';
import { CmsLayoutSSRFactory } from '@/src/cms/presentation/ssr/cms-layout-ssr-factory';
import { DynamicCMSPageView } from '@/src/cms/presentation/views/dynamic-cms-page-view';
import { CacheHeadersBuilder } from '@/src/shared/domain/models/cache-headers-builder';
import { BaseLayout } from '@/src/shared/presentation/components/base-layout';
import {
  PageMetadata,
  type PageMetadataProps
} from '@/src/shared/presentation/components/page-metadata';
import { useHreflang } from '@/src/shared/presentation/hooks/use-hreflang';
import { SsrDataProvider, useSsrData } from '@/src/shared/presentation/providers/ssr-data-provider';
import { SsrDataBuilder } from '@/src/shared/presentation/ssr/ssr-data-builder';
import { i18nNamespaces, type AppLocale } from '@/src/shared/utils/i18n';
import { isMobileUserAgent } from '@/src/shared/utils/user-agent';

import type { CMSPageContentViewModel } from '@/src/cms/presentation/types/cms-content.viewModel';
import type { SsrErrors } from '@/src/shared/presentation/ssr/ssr-data';
import type {
  PropsWithLocale,
  PropsWithReq,
  SsrDataPageProps
} from '@/src/shared/presentation/view-models/page.props';
import type { ServerResponse } from 'http';


const EXCLUDED_PATHS = [
  '.well-known/appspecific/com.chrome.devtools.json',
  'webpack.hot-update.json'
];

interface GetServerSidePropsContext extends PropsWithLocale, PropsWithReq {
  params: { path: string[] };
  res: ServerResponse;
}

export async function getServerSideProps({ locale, req, res, params }: GetServerSidePropsContext) {
  const language = locale as AppLocale;
  const cacheHeadersBuilder = new CacheHeadersBuilder(req.headers);
  const path = params.path.join('/');
  const builder = new SsrDataBuilder([
    new CmsContentForPathSSRFactory(path, language, cacheHeadersBuilder),
    new CmsLayoutSSRFactory(language, cacheHeadersBuilder)
  ]);
  const isRedirect = (errors: SsrErrors): boolean => {
    const contentForPathError = errors[CmsContentForPathSSRFactory.getKey()];
    return Boolean(contentForPathError) && contentForPathError instanceof CmsRedirection;
  };

  // Exclude certain paths
  if (EXCLUDED_PATHS.some((p) => path.includes(p))) {
    res.setHeader('Content-Type', 'application/json');
    res.end(JSON.stringify({}));
    return { props: {} };
  }

  await builder.createData();
  builder.serializeData();

  const redirect = isRedirect(builder.ssrErrors);

  if (redirect) {
    return {
      redirect: builder.ssrErrors[CmsContentForPathSSRFactory.getKey()]
    };
  }

  const contentError = builder.ssrErrors[CmsContentForPathSSRFactory.getKey()];
  let errorCode: number | null = null;

  if (isAxiosError(contentError)) {
    errorCode = Number(contentError?.code) || contentError?.response?.status || null;
  } else if (contentError) {
    errorCode = 500;
  } else {
    const pageContent = builder.ssrData[
      CmsContentForPathSSRFactory.getKey()
    ] as CMSPageContentViewModel | null;

    if (!pageContent) {
      errorCode = 404;
    }
  }

  if (errorCode) {
    res.statusCode = errorCode;
  }

  cacheHeadersBuilder.setHeaders(res);

  // Check if the page has all the desired metadata
  const pageContent = builder.ssrData[
    CmsContentForPathSSRFactory.getKey()
  ] as CMSPageContentViewModel | null;

  console.log('pageContent:', pageContent);
  if (!pageContent) {
    errorCode = 404;
  }

  return {
    props: {
      ...(await serverSideTranslations(language, i18nNamespaces)),
      ssrSerializedData: builder.ssrSerializedData,
      errorCode: errorCode,
      isMobile: isMobileUserAgent(req.headers['user-agent'])
    }
  };
}

export default function CMSPage({ ssrSerializedData, ...props }: SsrDataPageProps) {
  const builder = new SsrDataBuilder([new CmsContentForPathSSRFactory(), new CmsLayoutSSRFactory()]);

  return (
    <SsrDataProvider builder={builder} ssrSerializedData={ssrSerializedData}>
      <BaseLayout {...props}>
        <Metadata />

        <DynamicCMSPageView />
      </BaseLayout>
    </SsrDataProvider>
  );
}

function Metadata() {
  const content = useSsrData<CMSPageContentViewModel>(CmsContentForPathSSRFactory.getKey());
  const { setHrefLangMap } = useHreflang();
  const props: PageMetadataProps = useMemo(
    () => ({
      title: content.metaTitle,
      description: content.metaDescription,
      hrefLang: content.hrefLang,
      canonicalUrl: content.canonicalUrl,
      ogTitle: content.ogTitle,
      ogDescription: content.ogDescription,
      ogUrl: content.ogUrl,
      twitterTitle: content.twitterTitle,
      twitterDescription: content.twitterDescription,
      twitterImageAlt: content.twitterImageAlt,
      robots: content.robots
    }),
    [content]
  );

  useEffect(() => {
    setHrefLangMap(props.hrefLang);
  }, [setHrefLangMap, props.hrefLang]);

  return <PageMetadata {...props} />;
}
